import { RefObject, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { Box, ClickAwayListener, Stack } from "@mui/material"
import { TimePicker, PickersActionBarProps } from "@mui/x-date-pickers"
import { useAppLocalization } from "../../../../../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../../modules/localization/components/AppLocalized"
import { DeliveryTimeDay, DeliveryTimesField, DynamicConfigKey } from "../../../form/formTypes"
import { useDateTime } from "../../../../../../../modules/localization/useDateTime"
import { generateDisabledTimeSlots, sortedDeliveryTimes } from "../helpers"
import { StandardButton } from "../../../../../buttons/StandardButton"
import { useDeliveryTimeLimits } from "./form/useDeliveryTimeLimits"

const useStyles = makeStyles()(() => ({
    pickerButtonWrap: {
        padding: "1.5rem",
        gridColumn: 2,
        gridRow: 3,
    },
}))

type AddDeliveryTimePickerProps = Readonly<{
    handleClose: () => void
    timePickerAnchor: RefObject<HTMLButtonElement>
    openingHours: DeliveryTimeDay[]
}>

export function AddDeliveryTimePicker({ handleClose, timePickerAnchor, openingHours }: AddDeliveryTimePickerProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [{ value: deliveries }, , helpers] = useField<DeliveryTimesField>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const timelineList = deliveries.deliveryTimes
    const { minMaxTime } = useDeliveryTimeLimits(
        deliveries.deliveryDate ? dateTimeFactory.fromISO(deliveries.deliveryDate) : null,
        openingHours,
    )

    const [tempTime, setTempTime] = useState<DateTime | null>(null)

    useEffect(() => {
        const lastTime = timelineList.at(-1)?.time

        if (lastTime) {
            const parsed = DateTime.fromFormat(lastTime, "HH:mm")
            if (parsed.isValid) {
                setTempTime(parsed)
            } else {
                setTempTime(null)
            }
        } else {
            setTempTime(null)
        }
    }, [timelineList])

    const otherTimes = timelineList.map((el) => el.time)

    const shouldDisableTime = generateDisabledTimeSlots(otherTimes)

    const handleSaveTime = () => {
        if (tempTime) {
            const formattedTime = tempTime.toFormat("HH:mm")
            if (otherTimes.includes(formattedTime)) {
                enqueueSnackbar({
                    variant: "warning",
                    message: l10n.getString("create-order-delivery-times-same-time-error"),
                })
                return
            }

            const sortedTimes = sortedDeliveryTimes([...timelineList, { time: formattedTime, orderLines: [] }])

            helpers.setValue({
                ...deliveries,
                deliveryTimes: sortedTimes,
                activeTime: formattedTime,
            })
        }
        handleClose()
    }

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box>
                <TimePicker
                    value={tempTime}
                    onChange={(newTime) => setTempTime(newTime)}
                    open={true}
                    ampm={false}
                    slotProps={{
                        textField: { sx: { display: "none" } },
                        popper: {
                            anchorEl: timePickerAnchor.current,
                            placement: "bottom-start",
                            sx: {
                                "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
                                    width: "1px",
                                },
                                "& .MuiMultiSectionDigitalClock-root": {
                                    justifyContent: "center",
                                },
                            },
                        },
                    }}
                    slots={{
                        actionBar: (_props: PickersActionBarProps) => (
                            <Stack direction="row" className={classes.pickerButtonWrap}>
                                <LocalizedStrict id="create-order-delivery-times-add-button">
                                    <StandardButton onClick={handleSaveTime} size="medium">
                                        Add delivery
                                    </StandardButton>
                                </LocalizedStrict>
                            </Stack>
                        ),
                    }}
                    minutesStep={5}
                    shouldDisableTime={shouldDisableTime}
                    minTime={minMaxTime?.minTime ?? undefined}
                    maxTime={minMaxTime?.maxTime ?? undefined}
                />
            </Box>
        </ClickAwayListener>
    )
}
